import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { http, fileUrl } from "@/api";
import { useStore } from "@/context/StoreContext";
import { Button } from "@/components/ui/button";
import ProductCard from "@/components/ProductCard";
import { ArrowRight, Sparkles, Truck, Palette, Users2 } from "lucide-react";

export default function Home() {
  const { settings } = useStore();
  const [featured, setFeatured] = useState([]);
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    http.get("/products").then((r) => {
      const feat = r.data.filter((p) => p.featured);
      setFeatured(feat.length ? feat : r.data.slice(0, 8));
    });
    http.get("/categories").then((r) => setCategories(r.data));
  }, []);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative overflow-hidden" data-testid="hero">
        <div className="absolute inset-0 hero-grain opacity-70 pointer-events-none" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-10 pb-16 lg:pt-20 lg:pb-28 grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
          <div className="lg:col-span-6 relative z-10">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white border border-black/5 text-xs font-semibold uppercase tracking-widest">
              <Sparkles size={14} className="text-[var(--brand-primary)]" /> Venta al detalle
            </div>
            <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl font-bold leading-[1.05] mt-6" data-testid="hero-title">
              {settings?.hero_title || "Estructuras florales que aportan elegancia a tu ocasión."}
            </h1>
            <p className="mt-6 text-lg text-neutral-600 max-w-xl leading-relaxed" data-testid="hero-subtitle">
              {settings?.hero_subtitle}
            </p>
            <div className="mt-10 flex flex-wrap gap-3">
              <Link to="/catalogo">
                <Button className="btn-primary rounded-full h-14 px-8 text-base font-semibold" data-testid="hero-cta-catalog">
                  {settings?.hero_cta_primary || "Explorar catálogo"} <ArrowRight size={18} className="ml-2" />
                </Button>
              </Link>
              <Link to="/cotizacion">
                <Button variant="outline" className="rounded-full h-14 px-8 text-base font-semibold border-2 border-[var(--brand-accent)] text-[var(--brand-accent)] hover:bg-[var(--brand-accent)] hover:text-white" data-testid="hero-cta-quote">
                  {settings?.hero_cta_secondary || "Cotizar evento"}
                </Button>
              </Link>
            </div>
            <div className="mt-10 grid grid-cols-3 gap-4 max-w-md">
              {[
                { v: "500+", l: "Eventos atendidos" },
                { v: "48h", l: "Entrega inmediata" },
                { v: "COTIZACIONES", l: "Precios al mayor" },
              ].map((s) => (
                <div key={s.l}>
                  <div className="font-serif text-3xl font-bold text-[var(--brand-primary)]">{s.v}</div>
                  <div className="text-xs text-neutral-500 mt-1">{s.l}</div>
                </div>
              ))}
            </div>
          </div>
          <div className="lg:col-span-6 relative">
            <div className="relative grid grid-cols-6 gap-3">
              <div className="col-span-4 aspect-[4/5] rounded-3xl overflow-hidden shadow-2xl">
                <img
                  src={fileUrl(settings?.hero_images?.[0]) || "https://customer-assets-4nw71qhi.emergentagent.net/job_7d5f4f3e-3f3c-4221-a662-e0903fa95238/artifacts/caabc467b879e3cb_Gemini_Generated_Image_h8jrl6h8jrl6h8jr%20%281%29.webp"}
                  alt="Camino floral blanco"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="col-span-2 flex flex-col gap-3">
                <div className="aspect-square rounded-3xl overflow-hidden shadow-lg">
                  <img
                    src={fileUrl(settings?.hero_images?.[1]) || "https://customer-assets-4nw71qhi.emergentagent.net/job_7d5f4f3e-3f3c-4221-a662-e0903fa95238/artifacts/c205cc2cbb109a6c_Gemini_Generated_Image_jbjnzmjbjnzmjbjn.webp"}
                    alt="Ramo rosa"
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="aspect-square rounded-3xl overflow-hidden shadow-lg">
                  <img
                    src={fileUrl(settings?.hero_images?.[2]) || "https://customer-assets-4nw71qhi.emergentagent.net/job_7d5f4f3e-3f3c-4221-a662-e0903fa95238/artifacts/239e06e393098a4f_Gemini_Generated_Image_d87z9d87z9d87z9d.webp"}
                    alt="Centro de mesa verde"
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="bg-[var(--brand-primary)] text-white rounded-3xl p-5 flex-1 flex flex-col justify-center" data-testid="hero-shipping-box">
                  <Truck size={22} className="opacity-90" />
                  <div className="font-serif text-xl font-bold mt-2 leading-tight">Realizamos envíos a nivel nacional</div>
                  <div className="text-xs mt-1 opacity-90">Por la agencia de tu confianza</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* VALUE */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-5">
          {[
            { icon: Truck, t: "Entrega inmediata", d: "Productos en stock listos para despacho." },
            { icon: Users2, t: "Precios por volumen", d: "Escala mayorista automática en el carrito." },
            { icon: Palette, t: "Cualquier tonalidad", d: "Arreglos personalizados en el color que indiques." },
            { icon: Sparkles, t: "Calidad premium", d: "Flores artificiales que lucen reales." },
          ].map((f, i) => (
            <div key={f.t} className="bg-white rounded-2xl p-6 border border-black/5 anim-fade-up" style={{ animationDelay: `${i * 80}ms` }}>
              <f.icon size={24} className="text-[var(--brand-primary)]" />
              <div className="font-serif text-xl font-semibold mt-3">{f.t}</div>
              <div className="text-sm text-neutral-500 mt-2">{f.d}</div>
            </div>
          ))}
        </div>
      </section>

      {/* CATEGORIES */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="flex items-end justify-between mb-8">
          <div>
            <div className="text-xs uppercase tracking-widest text-[var(--brand-accent)] font-bold">Catálogo</div>
            <h2 className="font-serif text-3xl sm:text-4xl font-bold mt-2">Explora por categoría</h2>
          </div>
          <Link to="/catalogo" className="text-sm font-semibold text-[var(--brand-primary)] hidden sm:inline-flex items-center gap-1">
            Ver todo <ArrowRight size={14} />
          </Link>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {categories.map((c) => (
            <Link
              key={c.slug}
              to={`/catalogo/${c.slug}`}
              className="group relative rounded-2xl overflow-hidden aspect-[4/3] border border-black/5"
              data-testid={`category-tile-${c.slug}`}
            >
              {c.image ? (
                <img src={fileUrl(c.image)} alt={c.name} className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" loading="lazy" />
              ) : (
                <div className="absolute inset-0 bg-[var(--brand-accent)]" />
              )}
              <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/25 to-transparent" />
              <div className="absolute inset-0 flex flex-col justify-end p-6 text-white">
                <div className="text-xs uppercase tracking-widest opacity-80">{c.subcategories?.length ? `${c.subcategories.length} tipos` : "Ver productos"}</div>
                <div className="font-serif text-3xl font-bold leading-tight group-hover:translate-x-1 transition-transform">
                  {c.name}
                </div>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* FEATURED */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="flex items-end justify-between mb-8">
          <div>
            <div className="text-xs uppercase tracking-widest text-[var(--brand-accent)] font-bold">Destacados</div>
            <h2 className="font-serif text-3xl sm:text-4xl font-bold mt-2">Favoritos de la temporada</h2>
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {featured.map((p, i) => (
            <ProductCard key={p.id} product={p} index={i} />
          ))}
        </div>
      </section>

      {/* B2B BANNER */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16" data-testid="home-projects-section">
        <div className="relative rounded-3xl overflow-hidden p-10 lg:p-16 text-[#F5EFE3] border border-[#C9A96E]/30" style={{ background: "#1C2B22" }}>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center relative z-10">
            <div>
              <div className="flex items-center gap-3">
                <span className="h-px w-10 bg-[#C9A96E]" />
                <span className="text-xs uppercase tracking-[0.3em] text-[#C9A96E] font-semibold">Proyectos y eventos</span>
              </div>
              <h3 className="font-serif text-4xl sm:text-5xl font-semibold mt-5 leading-tight">
                Decoradores, eventistas y salones
              </h3>
              <p className="mt-5 text-[#F5EFE3]/75 max-w-lg leading-relaxed">
                {settings?.home_message_2 || "Precios mayoristas, cotizaciones rápidas y arreglos a medida en el color que indiques."}
              </p>
              <Link to="/cotizacion">
                <Button className="mt-8 rounded-full h-14 px-8 bg-[#C9A96E] text-[#1C2B22] hover:bg-[#F5EFE3] text-base font-semibold transition-colors" data-testid="home-b2b-cta">
                  Solicitar cotización <ArrowRight size={18} className="ml-2" />
                </Button>
              </Link>
            </div>
            <div className="relative aspect-[4/3] rounded-2xl overflow-hidden ring-1 ring-[#C9A96E]/40">
              <img
                src={fileUrl(settings?.projects_image) || "https://images.unsplash.com/photo-1525441273400-056e9c7517b3?auto=format&fit=crop&w=1200&q=80"}
                alt="Salón decorado con arreglos florales"
                className="w-full h-full object-cover"
                data-testid="home-projects-image"
              />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
